import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HttpUtilsService, QueryParamsModel } from '../_base/crud';
import { select, Store } from '@ngrx/store';
import { AppState } from '../reducers';
import { currentAuthToken, currentUser } from '../auth/_selectors/auth.selectors';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { isEqual, uniqWith } from 'lodash-es';
import { MatDialog } from '@angular/material/dialog';
import { FollowUpDialogComponent } from '../../views/pages/follow-up/follow-up-dialog/follow-up-dialog.component';
import * as moment from 'moment';

@Injectable()
export class FollowUpService {
  private authToken;
  private companyId;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
    public dialog: MatDialog,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  private static getReasonsListQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.reason) fromString += `&name_has=${filter.reason}`;
    }

    return fromString;
  }

  getFollowUpById(type, transaction_id: number) {
    const url =
      environment.FOLLOWUP_BASE +
      `/companies/${this.companyId}/followup/transaction/${transaction_id}?type=${type}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers });
  }

  getFollowUpByContactIds(ids) {
    let param = '';
    let url = environment.FOLLOWUP_BASE + `/companies/${this.companyId}/followup?`;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    ids = uniqWith(ids, isEqual);
    ids.forEach((element) => (param = param + '&contact_id=' + element));
    url = url + param + '&type=ACTIVE';
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  getHistory(id) {
    const url = environment.FOLLOWUP_BASE + `/companies/${this.companyId}/followup/${id}/history`;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  getFollowUpByFilters(
    from_date?,
    to_date?,
    responsible_user_id?,
    type?,
    team_followups?,
    contact?,
  ) {
    let param = '';
    let url = environment.FOLLOWUP_BASE + `/companies/${this.companyId}/followup?`;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    if (from_date) {
      param = param + '&from_date=' + from_date;
    }
    if (to_date) {
      param = param + '&to_date=' + to_date;
    }
    if (responsible_user_id) {
      param = param + '&responsible_user_id=' + responsible_user_id;
    }
    if (type) {
      param = param + '&transaction_type=' + type;
    }
    if (team_followups) {
      param = param + '&team_followups=' + team_followups;
    }
    if (contact) {
      param = param + '&contact=' + contact;
    }
    url = url + param;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  getFollowUpPaginatedByFilters(queryParams: QueryParamsModel) {
    const url = environment.FOLLOWUP_BASE + `/companies/${this.companyId}/followup/paginated-list?`;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const httpParams = this.httpUtils.getFindHTTPParams(queryParams);
    return this.http.get<any>(url, { headers: httpHeaders, params: httpParams });
  }

  createFollowUp(followUp) {
    const url = environment.FOLLOWUP_BASE + `/companies/${this.companyId}/followup`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<any>(url, followUp, { headers });
  }

  updateFollowUp(followUp) {
    const url = environment.FOLLOWUP_BASE + `/companies/${this.companyId}/followup/${followUp.id}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.put<any>(url, followUp, { headers });
  }

  searchReasons(search_term: string): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.FOLLOWUP_BASE +
      '/companies/' +
      this.companyId +
      '/followup/reason?search_term=' +
      search_term;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  findReasons(queryParams: QueryParamsModel): Observable<any> {
    const url = environment.FOLLOWUP_BASE + '/companies/' + this.companyId + '/followup/reason';
    const params = new HttpParams({
      fromString: FollowUpService.getReasonsListQueryString(queryParams),
    });
    return this.http.get<any>(url, { params });
  }

  getFollowupComments(followup_id: number) {
    const url = `${environment.FOLLOWUP_BASE}/companies/${this.companyId}/followup/${followup_id}/comments`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers });
  }

  postFollowUpComment(type, followup_id: number, comment) {
    const url = `${environment.FOLLOWUP_BASE}/companies/${this.companyId}/followup/${followup_id}/comments?type=${type}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<any>(url, comment, { headers });
  }

  updateFollowupComment(
    type,
    transaction_comment_id,
    followup_comment_id,
    followup_id: number,
    comment,
  ) {
    const url =
      `${environment.FOLLOWUP_BASE}/companies/${this.companyId}/followup/${followup_id}` +
      `/comments?type=${type}&transaction_comment_id=${transaction_comment_id}&followup_comment_id=${followup_comment_id}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.put<any>(url, comment, { headers });
  }

  deleteFollowUpComment(
    type,
    transaction_comment_id,
    followup_comment_id,
    followup_id,
    transaction_id,
  ) {
    const url =
      `${environment.FOLLOWUP_BASE}/companies/${this.companyId}/followup/${followup_id}` +
      `/comments?type=${type}&transaction_comment_id=${transaction_comment_id}&followup_comment_id=${followup_comment_id}&transaction_id=${transaction_id}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<any>(url, { headers });
  }

  getWorkflowQueueById(workflow_id) {
    const url = `${environment.EVENT_BASE}/companies/${this.companyId}/workflow_queues/${workflow_id}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers });
  }

  getCard(workflow_id, workflow_action_id, workflow_queue_id) {
    const url = `${environment.EVENT_BASE}/companies/${this.companyId}/workflow/${workflow_id}?workflow_action_id=${workflow_action_id}&workflow_queue_id=${workflow_queue_id}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers });
  }

  openDialog(data) {
    return this.dialog.open(FollowUpDialogComponent, {
      data,
      width: '1200px',
    });
  }

  getFollowUpDashboard(userId, date) {
    const url =
      environment.FOLLOWUP_BASE +
      `/companies/${this.companyId}/followup/dashboard?user_id=${userId}&due_date=${moment(
        date,
      ).format('YYYY-MM-DD')}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers });
  }
}
